import { supabase } from '../supabase';

export type StorageBucket = 'event-flyers' | 'event-images' | 'gallery-images';

export interface UploadResult {
  url: string;
  path: string;
}

/**
 * Upload a file to Supabase Storage
 * @param bucket - The storage bucket to upload to
 * @param file - The file to upload
 * @param path - Optional path within the bucket (defaults to timestamp-filename)
 * @returns The public URL and path of the uploaded file
 */
export async function uploadFile(
  bucket: StorageBucket,
  file: File,
  path?: string
): Promise<UploadResult> {
  // Generate a unique path if not provided
  const timestamp = Date.now();
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = path || `${timestamp}-${sanitizedName}`;

  // Upload the file
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    throw new Error(`Failed to upload file: ${error.message}`);
  }

  // Get the public URL
  const { data: { publicUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(data.path);

  return {
    url: publicUrl,
    path: data.path,
  };
}

/**
 * Delete a file from Supabase Storage
 * @param bucket - The storage bucket
 * @param path - The path of the file to delete
 */
export async function deleteFile(bucket: StorageBucket, path: string): Promise<void> {
  const { error } = await supabase.storage.from(bucket).remove([path]);

  if (error) {
    throw new Error(`Failed to delete file: ${error.message}`);
  }
}

/**
 * Get the public URL for a file
 * @param bucket - The storage bucket
 * @param path - The path of the file
 * @returns The public URL
 */
export function getPublicUrl(bucket: StorageBucket, path: string): string {
  const { data: { publicUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  return publicUrl;
}

/**
 * Extract the bucket and path from a Supabase Storage URL
 * @param url - The full Supabase Storage URL
 * @returns Object with bucket and path, or null if not a valid storage URL
 */
export function parseStorageUrl(url: string): { bucket: StorageBucket; path: string } | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');

    // URL format: /storage/v1/object/public/{bucket}/{path}
    if (pathParts.length >= 6 && pathParts[1] === 'storage' && pathParts[4] === 'public') {
      const bucket = pathParts[5] as StorageBucket;
      const path = pathParts.slice(6).join('/');

      return { bucket, path };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Replace an existing file (delete old, upload new)
 * @param bucket - The storage bucket
 * @param oldUrl - The URL of the old file to replace
 * @param newFile - The new file to upload
 * @returns The public URL and path of the uploaded file
 */
export async function replaceFile(
  bucket: StorageBucket,
  oldUrl: string | null,
  newFile: File
): Promise<UploadResult> {
  // Delete old file if it exists and is a storage URL
  if (oldUrl) {
    const parsed = parseStorageUrl(oldUrl);
    if (parsed && parsed.bucket === bucket) {
      try {
        await deleteFile(bucket, parsed.path);
      } catch (error) {
        console.warn('Failed to delete old file:', error);
        // Continue with upload even if delete fails
      }
    }
  }

  // Upload new file
  return uploadFile(bucket, newFile);
}

/**
 * Validate file type and size
 * @param file - The file to validate
 * @param bucket - The bucket to validate against
 * @returns Error message if invalid, null if valid
 */
export function validateFile(file: File, bucket: StorageBucket): string | null {
  const maxSizes: Record<StorageBucket, number> = {
    'event-flyers': 10 * 1024 * 1024, // 10MB
    'event-images': 5 * 1024 * 1024, // 5MB
    'gallery-images': 5 * 1024 * 1024, // 5MB
  };

  const allowedTypes: Record<StorageBucket, string[]> = {
    'event-flyers': ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'],
    'event-images': ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
    'gallery-images': ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
  };

  // Check file size
  if (file.size > maxSizes[bucket]) {
    const maxMB = Math.round(maxSizes[bucket] / 1024 / 1024);
    return `File size must be less than ${maxMB}MB`;
  }

  // Check file type
  if (!allowedTypes[bucket].includes(file.type)) {
    return `File type ${file.type} is not allowed for this upload`;
  }

  return null;
}
